/**
 * Query params that carry a credential or a handoff target rather than attribution: the OAuth `code`/`state`
 * (and `error`) on /auth/discord/callback, the loopback `redirect_uri` on /auth/desktop, the
 * `/reset-password` and squad invite `token`. A removal list, not an allow-list, so the UTMs and click ids
 * that attribution needs survive. Matched case-insensitively: `Code=` is the same credential as `code=`.
 * No page of ours reads any of these names as a campaign param.
 */
const SENSITIVE_QUERY_PARAMS = new Set(['code', 'state', 'error', 'token', 'redirect_uri']);

/**
 * `rawUrl` without the params above and without its fragment, for a URL that is about to leave for a third
 * party (the OpenAI Conversions API `source_url`). The fragment goes too: it is never attribution, and it is
 * where an implicit-flow handoff would put a token. A URL with nothing to remove comes back byte for byte —
 * re-serializing it would re-encode every UTM value. Anything `URL` cannot parse comes back untouched; the
 * caller still applies its own origin check.
 *
 * It only reads the query: a credential carried in the path (the `/squad/accept/<token>` form) is not
 * something a param list can find.
 */
export function stripSensitiveQuery(rawUrl: string): string {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return rawUrl;
  }

  const sensitiveKeys = Array.from(url.searchParams.keys()).filter((key) => SENSITIVE_QUERY_PARAMS.has(key.toLowerCase()));
  if (sensitiveKeys.length === 0 && !url.hash) return rawUrl;

  sensitiveKeys.forEach((key) => url.searchParams.delete(key));
  url.hash = '';
  return url.toString();
}
